import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CartContext } from '../App';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { CheckCircle, Package, ArrowRight, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrderConfirmationPage() {
  const { orderId } = useParams();
  const { clearCart } = useContext(CartContext);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    clearCart();
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrder(res.data);
    } catch (err) {
      console.error('Failed to load order:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <Loader2 className="h-12 w-12 text-[#78BE20] mx-auto animate-spin" />
          <p className="text-muted-foreground mt-4">Loading order details...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center" data-testid="order-confirmation-page">
        <CheckCircle className="h-20 w-20 text-[#78BE20] mx-auto" />
        <h1 className="text-3xl font-black text-foreground uppercase tracking-tight mt-6">
          Order Placed!
        </h1>
        <p className="text-muted-foreground text-lg mt-3">
          Thank you for your order. You will receive a confirmation email shortly.
        </p>

        {order && (
          <div className="bg-card border border-border rounded-sm p-6 text-left mt-8">
            <h2 className="font-bold text-foreground mb-4">Order Details</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Order Number</span>
                <span className="font-mono font-medium">{order.order_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Payment Method</span>
                <span className="capitalize">{order.payment_method === 'account' ? 'On Account' : order.payment_method || 'Cash/Invoice'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Items</span>
                <span>{order.items?.length || 0} item(s)</span>
              </div>
              {order.payment_due_date && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Payment Due</span>
                  <span>{new Date(order.payment_due_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                </div>
              )}
              <div className="border-t border-border pt-3 flex justify-between font-bold">
                <span>Total</span>
                <span className="text-[#78BE20] font-mono">${(order.total || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
          <Link to="/account">
            <Button className="bg-[#78BE20] text-black hover:bg-[#65a31b]">
              <Package className="h-4 w-4 mr-2" />
              View My Orders
            </Button>
          </Link>
          <Link to="/products">
            <Button variant="outline" className="border-zinc-700">
              Continue Shopping
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </Layout>
  );
}
